import UsersFilledSvg from '@/assets/svgs/main/users-filled.svg';
import useToast from '@/containers/ToastMessage/hooks/useToast';
import {
  useAssignUserToAccount,
  useUnassignUserFromAccount,
} from '@/containers/Accounts/api';
import { useUpdateUser } from '@/containers/Users/api';
import { UpdateUserPayload } from '@/containers/Users/types';
import { selectCurrentAccount, selectSubAccountsByRoot } from '@/lib/redux/app.selectors';
import { Account, SubAccount } from '@/lib/redux/app.slice';
import { AssignmentList } from '@/shared/components/AssignmentList';
import { SearchBar } from '@/shared/components/SearchBar/SearchBar';
import { UserTimezoneSelect } from '@/shared/components/UserTimezoneSelect';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modals } from '../all-modals';
import AccountItemView from '../CreateAccountUserModal/AccountItemView';
import ModalBody from '../ModalBody';
import ModalContainer from '../ModalContainer';
import ModalHeader from '../ModalHeader';
import { EditUserDetailsModalParams, ModalProps } from '../types';
import useModals from '../useModals';

interface EditUserDetailsModalProps extends ModalProps {
  params?: EditUserDetailsModalParams;
}

const modalName = Modals.EditUserDetailsModal;

const USER_TYPE_OPTIONS = [
  { value: 'USER', label: 'User' },
  { value: 'ADMIN-USER', label: 'Admin User' },
] as const;

const EditUserDetailsModal = ({
  className,
  variant = 'default',
  params,
}: EditUserDetailsModalProps) => {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const { onClose, isModalOpen } = useModals({
    modalName,
  });

  const toast = useToast();
  const currentAccount = useSelector(selectCurrentAccount);
  const subAccountsByRoot = useSelector(selectSubAccountsByRoot);

  // Modal specific details
  const { userId, userDetails } = params || {};

  // Form state - initialize from userDetails
  const [firstName, setFirstName] = useState(userDetails?.first_name || '');
  const [lastName, setLastName] = useState(userDetails?.last_name || '');
  const [email, setEmail] = useState(userDetails?.email || '');
  const [userType, setUserType] = useState<'USER' | 'ADMIN-USER'>(
    (userDetails?.user_type as 'USER' | 'ADMIN-USER') || 'USER'
  );
  const [timezone, setTimezone] = useState(userDetails?.local_timezone || 'US/Eastern');
  const [assignedAccountIds, setAssignedAccountIds] = useState<string[]>(
    userDetails?.root_accounts || []
  );
  const [accountSearchText, setAccountSearchText] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const updateUserMutation = useUpdateUser();
  const assignUserMutation = useAssignUserToAccount();
  const unassignUserMutation = useUnassignUserFromAccount();

  // Reset form when modal opens/closes or userDetails changes
  useEffect(() => {
    if (isModalOpen && userDetails) {
      setFirstName(userDetails.first_name);
      setLastName(userDetails.last_name);
      setEmail(userDetails.email);
      setUserType((userDetails.user_type as 'USER' | 'ADMIN-USER') || 'USER');
      setTimezone(userDetails.local_timezone);
      setAssignedAccountIds(userDetails.root_accounts || []);
      setAccountSearchText('');
      setErrors({});
    }
  }, [isModalOpen, userDetails]);

  // Determine if we should show account assignment UI
  // Show if the current account has sub-accounts, regardless of whether it's root or sub
  const shouldShowAccountAssignment = useMemo(() => {
    if (!currentAccount) return false;
    const subs = subAccountsByRoot[currentAccount.account_id] || [];
    return subs.length > 0;
  }, [currentAccount, subAccountsByRoot]);

  // Build list of assignable accounts
  const assignableAccounts = useMemo<Account[]>(() => {
    if (!shouldShowAccountAssignment || !currentAccount) return [];

    const accounts: Account[] = [currentAccount];
    const subs = subAccountsByRoot[currentAccount.account_id] || [];
    subs.forEach((sub: SubAccount) => {
      accounts.push({
        account_id: sub.account_id,
        account_name: sub.account_name,
        account_type: 'sub',
        parent_account_id: currentAccount.account_id,
      });
    });

    return accounts;
  }, [shouldShowAccountAssignment, currentAccount, subAccountsByRoot]);

  // Filter accounts based on search
  const filteredAccounts = useMemo(() => {
    if (!accountSearchText) return assignableAccounts;
    return assignableAccounts.filter((account) =>
      account.account_name.toLowerCase().includes(accountSearchText.toLowerCase())
    );
  }, [assignableAccounts, accountSearchText]);

  // Separate assigned and available accounts
  const assignedAccounts = useMemo(() => {
    return filteredAccounts.filter((account) =>
      assignedAccountIds.includes(account.account_id)
    );
  }, [filteredAccounts, assignedAccountIds]);

  const availableAccounts = useMemo(() => {
    return filteredAccounts.filter(
      (account) => !assignedAccountIds.includes(account.account_id)
    );
  }, [filteredAccounts, assignedAccountIds]);

  const handleAssignAccount = (account: Account) => {
    setAssignedAccountIds((prev) => [...prev, account.account_id]);
  };

  const handleUnassignAccount = (account: Account) => {
    // Prevent unassigning the last account
    if (assignedAccountIds.length === 1) {
      toast.showError('Cannot unassign user from their last account');
      return;
    }
    setAssignedAccountIds((prev) => prev.filter((id) => id !== account.account_id));
  };

  const handleAssignAllAccounts = () => {
    const allIds = assignableAccounts.map((acc) => acc.account_id);
    // Additive update to preserve any accounts not in the assignable list
    setAssignedAccountIds((prev) => Array.from(new Set([...prev, ...allIds])));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.showError('Please fix the errors in the form');
      return;
    }

    if (!userId) {
      toast.showError('User ID is missing');
      return;
    }

    const payload: UpdateUserPayload = {
      email: email.trim(),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      user_type: userType,
      local_timezone: timezone,
      root_accounts: assignedAccountIds,
      identities: userDetails?.identities || [],
      user_hash: null,
      user_id: null,
      username: null,
    };

    try {
      // 1. Update User Details
      await updateUserMutation.mutateAsync({
        userId,
        payload,
      });

      // 2. Handle Account Assignments
      // We only proceed if user update was successful
      const initialAccounts = userDetails?.root_accounts || [];
      const currentAccounts = assignedAccountIds;

      // Calculate Diff (Ids)
      const accountsToAddIds = currentAccounts.filter(
        (id) => !initialAccounts.includes(id)
      );
      const accountsToRemoveIds = initialAccounts.filter(
        (id) => !currentAccounts.includes(id)
      );

      // We need Account Names for error reporting
      // Optimistically try to find them in assignableAccounts, otherwise fallback to ID
      const getAccountName = (id: string) => {
        const account = assignableAccounts.find((a) => a.account_id === id);
        return account ? account.account_name : `Account ${id}`;
      };

      const promises: Promise<unknown>[] = [];
      const failedOperations: string[] = [];

      // Add Assignments
      accountsToAddIds.forEach((accountId) => {
        promises.push(
          assignUserMutation
            .mutateAsync({ userId, accountId })
            .catch(() => failedOperations.push(`Assign: ${getAccountName(accountId)}`))
        );
      });

      // Remove Assignments
      accountsToRemoveIds.forEach((accountId) => {
        promises.push(
          unassignUserMutation
            .mutateAsync({ userId, accountId })
            .catch(() => failedOperations.push(`Unassign: ${getAccountName(accountId)}`))
        );
      });

      // Wait for all account operations
      await Promise.allSettled(promises);

      if (failedOperations.length > 0) {
        toast.showSuccess('User details updated');
        toast.showHtmlError(
          <div>
            <p>Failed to update some account associations:</p>
            <ul className="mt-1 list-disc pl-4">
              {failedOperations.map((op, idx) => (
                <li key={idx}>{op}</li>
              ))}
            </ul>
          </div>
        );
      } else {
        toast.showSuccess('User updated successfully');
      }

      onClose();
    } catch (error) {
      console.error('Failed to update user:', error);
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to update user';
      toast.showError(errorMessage);
    }
  };

  const userName = userDetails
    ? `${userDetails.first_name} ${userDetails.last_name}`
    : 'User';

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />
      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        isSearchEmpty={true}
        variant={variant}
        onClose={onClose}
        shouldCloseOnEscape>
        <form onSubmit={handleSubmit} className="flex h-full flex-col overflow-hidden">
          {/* Stick to top */}
          <ModalHeader
            modalName={modalName}
            closeAction="state"
            icon={<UsersFilledSvg className="h-5 w-5" />}
            title={`Edit ${userName}`}
          />

          {/* Take up remaining space and enable inside-scrolling */}
          <ModalBody ref={modalBodyRef} className="px-5 !py-0">
            {/* Spacing for consistency */}
            <div className="h-6" />

            {/* Form Fields */}
            <div className="flex flex-col gap-4 pb-6">
              {/* Row 1: First Name & Last Name */}
              <div className="grid grid-cols-2 gap-4">
                {/* First Name */}
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">First Name</span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={classNames('input input-bordered w-full', {
                      'input-error': errors.firstName,
                    })}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && (
                    <label className="label">
                      <span className="label-text-alt text-error">
                        {errors.firstName}
                      </span>
                    </label>
                  )}
                </div>

                {/* Last Name */}
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Last Name</span>
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={classNames('input input-bordered w-full', {
                      'input-error': errors.lastName,
                    })}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && (
                    <label className="label">
                      <span className="label-text-alt text-error">{errors.lastName}</span>
                    </label>
                  )}
                </div>
              </div>

              {/* Row 2: Email & User Type */}
              <div className="grid grid-cols-2 gap-4">
                {/* Email */}
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Email</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={classNames('input input-bordered w-full', {
                      'input-error': errors.email,
                    })}
                    placeholder="Enter email"
                  />
                  {errors.email && (
                    <label className="label">
                      <span className="label-text-alt text-error">{errors.email}</span>
                    </label>
                  )}
                </div>

                {/* User Type */}
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">User Type</span>
                  </label>
                  <select
                    value={userType}
                    onChange={(e) => setUserType(e.target.value as 'USER' | 'ADMIN-USER')}
                    className="select select-bordered w-full">
                    {USER_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 3: Timezone */}
              <div className="grid grid-cols-1 gap-4">
                {/* Timezone */}
                <UserTimezoneSelect value={timezone} onChange={setTimezone} />
              </div>

              {/* Account Assignment (Conditional) */}
              {shouldShowAccountAssignment && (
                <div className="mt-4">
                  <label className="label">
                    <span className="label-text">Account Assignment</span>
                  </label>

                  {/* Search */}
                  <div className="mb-4">
                    <SearchBar
                      search={accountSearchText}
                      setSearch={setAccountSearchText}
                      placeholder="Search accounts..."
                      debounceMs={300}
                      ariaLabel="Search accounts"
                      className="w-full"
                    />
                  </div>

                  {/* Assignment List */}
                  <AssignmentList
                    assignedItems={assignedAccounts}
                    availableItems={availableAccounts}
                    LineItemView={AccountItemView}
                    onAssign={handleAssignAccount}
                    onUnassign={handleUnassignAccount}
                    onAssignAll={handleAssignAllAccounts}
                    isLoading={false}
                    searchText={accountSearchText}
                    emptyMessage="No accounts found"
                    getItemKey={(account) => account.account_id}
                    canUnassignItem={() => assignedAccountIds.length > 1}
                    getUnassignTooltip={(_account) =>
                      assignedAccountIds.length > 1
                        ? 'Remove'
                        : 'Cannot unassign user from their last account'
                    }
                  />
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-6 flex items-center justify-end gap-3">
                <button type="button" onClick={onClose} className="btn btn-outline">
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updateUserMutation.isPending}>
                  {updateUserMutation.isPending && (
                    <span className="loading loading-spinner"></span>
                  )}
                  Save Changes
                </button>
              </div>
            </div>
          </ModalBody>
        </form>
        <form method="dialog" className="modal-backdrop">
          <button onClick={onClose}>close</button>
        </form>
      </ModalContainer>
    </div>
  );
};

export default EditUserDetailsModal;
